import type { Draft } from 'immer';
import produce from 'immer';
import { omit, pull } from 'lodash';

import type { MarketPollingData } from '@swf/sports/types/market';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';
import updateMarket from '@swf/sports/utils/updateMarket';

import { defaultState } from './defaultState';
import type { EventPageTemplateView, MarketGroupsArray } from './types';

type State = typeof defaultState;

export const reducers = {
  clear: (): State => produce(defaultState, (draft) => draft),
  eventPageRequested: (state: State, pageChanged: boolean = false): State => ({
    ...state,
    isLoading: true,
    error: undefined,
    errorCode: undefined,
    events: pageChanged ? {} : state.events,
    hasPreloaded: !pageChanged && state.hasPreloaded
  }),
  eventPageLoadSuccess: (state: State, payload: EventPageTemplateView): State => ({
    ...state,
    ...payload,
    isLoading: false,
    hasPreloaded: true
  }),
  eventPageLoadFailure: (state: State, payload: number): State => ({
    ...state,
    isLoading: false,
    hasPreloaded: true,
    error: true,
    errorCode: payload
  }),
  marketsUpdated: (state: State, { data: marketUpdates, closed }: MarketPollingData) =>
    typeof state.error === 'undefined'
      ? produce(state, (draft) => {
          // Remove any closed markets from the store

          if (closed.length) {
            draft.markets = omit(draft.markets, closed);

            draft.marketsFeature = draft.marketsFeature.filter((group) => {
              group.templates = group.templates.filter((template) => {
                if (isCompoundMarketTemplate(template)) {
                  template.marketIds = pull(template.marketIds, ...closed);

                  return Boolean(template.marketIds.length);
                }

                return !closed.includes(template.marketId);
              });

              if (group.type === 'BET_BUILDER') {
                group.popularBetBuilders = group.popularBetBuilders.filter((pbb) =>
                  pbb.runnersIds.every(({ marketId }) => !closed.includes(marketId))
                );
              }

              return Boolean(group.templates.length);
            }) as Draft<MarketGroupsArray>;
          }

          // Update the markets with new data
          Object.entries(marketUpdates).forEach(([marketUpdateId, marketUpdate]) => {
            const market = draft.markets[marketUpdateId];

            if (market && marketUpdate) updateMarket(market, marketUpdate);
          });

          return draft;
        })
      : state
};
